import * as Sentry from '@sentry/react';
import _ from 'lodash';
import BaseStore from 'vendor/cnpm/fluxible.v0-4/addons/BaseStore';

import user from 'js/lib/user';

import CourseViewGrade from 'pages/open-course/common/models/courseViewGrade';
// TODO: Migrate off of Backbone
// eslint-disable-next-line no-restricted-imports
import type ItemGrade from 'pages/open-course/common/models/itemGrade';
import type ItemGroupGrade from 'pages/open-course/common/models/itemGroupGrade';
import type { TrackId } from 'pages/open-course/common/models/tracks';

import { gradePercentageValue } from 'bundles/course-v2/utils/gradePercentageValue';
import type { GradedAssignmentGroupGrade } from 'bundles/learner-progress/types/GradedAssignmentGroup';

const SERIALIZED_PROPS: Array<keyof CourseViewGradeStore$DehydratedState> = ['rawCourseViewGrade'];

type CourseViewGradeStore$DehydratedState = {
  rawCourseViewGrade: any;
};

type CourseViewGradeType = typeof CourseViewGrade;
type RawCourseViewGrade = any;
type ItemOutcomeOverrides = any;

class CourseViewGradeStore extends BaseStore {
  static storeName = 'CourseViewGradeStore';

  static handlers = {
    LOAD_COURSE_VIEW_GRADE: 'handleLoadCourseViewGrade',
    ERROR_LOADING_COURSE_VIEW_GRADE: 'handleCourseViewGradeLoadingError',
  };

  handleLoadCourseViewGrade(rawCourseViewGrade: any) {
    this.rawCourseViewGrade = rawCourseViewGrade;
    this.courseViewGrade = new CourseViewGrade(rawCourseViewGrade);
    this.emitChange();
  }

  handleCourseViewGradeLoadingError({ error, courseId }: { error: Error; courseId: string }) {
    Sentry.captureException('Failed to load course view grade', { extra: { error } });

    this.error = error;

    const userInfo = user.get();
    const emptyCourseViewGrade = {
      verifiedGrade: 0,
      overallGrade: 0,
      id: `${userInfo.id}~${courseId}`,
      userId: userInfo.id,
      passingState: 'notPassed',
      itemGrades: {},
      itemOutcomeOverrides: {},
      trackAttainments: {},
      itemGroupGrades: {},
      gradedAssignmentGroups: {},
    };

    this.rawCourseViewGrade = emptyCourseViewGrade;
    this.courseViewGrade = new CourseViewGrade(emptyCourseViewGrade);
  }

  declare courseViewGrade: CourseViewGradeType;

  declare rawCourseViewGrade: RawCourseViewGrade;

  error?: Error;

  dehydrate(): CourseViewGradeStore$DehydratedState {
    return _.pick(this, ...SERIALIZED_PROPS);
  }

  rehydrate(state: CourseViewGradeStore$DehydratedState) {
    Object.assign(this, _.pick(state, ...SERIALIZED_PROPS));
    this.courseViewGrade = new CourseViewGrade(this.rawCourseViewGrade);
  }

  hasLoaded(): boolean {
    return !!this.courseViewGrade;
  }

  getCourseViewGrade(): CourseViewGradeType {
    return this.courseViewGrade;
  }

  isPassable(): boolean {
    const courseViewGrade = this.getCourseViewGrade();
    return !!courseViewGrade && courseViewGrade.isPassable();
  }

  /**
   * @returns {bool} True if the learner has passed the course, regardless of verification status.
   */
  hasPassedCourseUnverified(): boolean {
    const courseViewGrade = this.getCourseViewGrade();
    return !!courseViewGrade && courseViewGrade.hasPassed();
  }

  /**
   * @returns {bool} True if the learner has passed the course with verification.
   */
  hasPassedCourseWithVerification(): boolean {
    const courseViewGrade = this.getCourseViewGrade();
    return !!courseViewGrade && courseViewGrade.hasPassedCourseWithVerification();
  }

  /**
   * @returns {bool} True if the learner has passed the given track of the course with verification.
   */
  hasPassedTrackWithVerification(trackId: TrackId): boolean {
    const courseViewGrade = this.getCourseViewGrade();
    return !!courseViewGrade && courseViewGrade.hasPassedTrack(trackId);
  }

  /**
   * @returns {bool} True if the learner has passed the given track, regardless of verification status.
   */
  hasPassedTrackUnverified(trackId: TrackId): boolean {
    const courseViewGrade = this.getCourseViewGrade();
    return !!courseViewGrade && courseViewGrade.hasPassedTrack(trackId);
  }

  /**
   * @returns {Number} The user's final course grade, rounded to 1 decimal place (ex. 97.2%)
   */
  getFinalPercentCourseGrade(): number | string | undefined {
    const courseViewGrade = this.getCourseViewGrade();
    const percentCourseGrade: number | undefined = !!courseViewGrade && courseViewGrade.get('overallGrade');

    if (percentCourseGrade) {
      return gradePercentageValue({ grade: percentCourseGrade });
    }

    return undefined;
  }

  isItemPassed(itemId: string): boolean {
    const itemGrade = this.getItemGrade(itemId);
    return !!itemGrade && itemGrade.isPassing();
  }

  getItemGrade(itemId: string): typeof ItemGrade {
    const courseViewGrade = this.getCourseViewGrade();
    if (courseViewGrade) {
      return courseViewGrade.getItemGrade(itemId);
    }

    return undefined;
  }

  isItemGroupPassed(itemGroupId: string): boolean {
    const itemGroupGrade = this.getItemGroupGrade(itemGroupId);
    return !!itemGroupGrade && itemGroupGrade.isPassing;
  }

  getItemGroupOverallPassedCount(itemGroupId: string): number {
    const itemGroupGrade = this.getItemGroupGrade(itemGroupId);

    if (itemGroupGrade) {
      return itemGroupGrade.overallPassedCount;
    }

    return 0;
  }

  getItemGroupGrade(itemGroupId: string): ItemGroupGrade | undefined {
    const courseViewGrade = this.getCourseViewGrade();
    if (courseViewGrade) {
      return courseViewGrade.getItemGroupGrade(itemGroupId);
    }

    return undefined;
  }

  getGradedAssignmentGroupGrades(): { [id: string]: GradedAssignmentGroupGrade } | undefined {
    const courseViewGrade = this.getCourseViewGrade();
    if (courseViewGrade) {
      return courseViewGrade.get('gradedAssignmentGroups');
    }

    return undefined;
  }

  getRelativePassingState(itemId: string): any {
    const itemGrade = this.getItemGrade(itemId);
    return !!itemGrade && itemGrade.getRelativePassingState();
  }

  getItemOutcomeOverrides(): ItemOutcomeOverrides {
    const courseViewGrade = this.getCourseViewGrade();
    if (courseViewGrade && courseViewGrade.get('itemOutcomeOverrides')) {
      return courseViewGrade.get('itemOutcomeOverrides');
    }

    return {};
  }
}

export default CourseViewGradeStore;
